import _ from 'lodash';
import moment from 'moment-timezone';
 
export const scheduleFieldsEnum = {
  name: {
    fieldId: 'name',
    required: () => { return true },
  },
  type: {
    fieldId: 'type',
    required: () => { return true },
    readOnly: (schedule) => { return !!schedule?.id },
  },
  daily: {
    fieldId: 'daily',
    required: (schedule) => { return schedule.type === 'daily' },
    toDTO: (schedule, value) => {
      if (schedule.type !== 'daily' || !schedule.offset) { return undefined; }
      const offsets = schedule.offset.map(o => {
        if (o > 86400) {
          return o % 86400;
        }
        if (o < 0) {
          return o + 86400;
        }
        return o;
      });
      return { offsets };
    },
  },
  start_time: {
    fieldId: 'start_time',
    required: () => { return false },
    toDTO: (schedule, value) => {
      if (!value) { return undefined; }
      return value.format();
    },
  },
  end_time: {
    fieldId: 'end_time',
    required: () => { return false },
    toDTO: (schedule, value) => {
      // Return null (not undefined) when cleared so the diff logic in
      // getScheduleDataForBackend keeps the field and onSave can map it to
      // clear_end_time=true. Returning undefined would drop the field and
      // the backend would preserve the previous end date.
      if (!value) { return null; }
      return value.format();
    },
  },
  timezone: {
    fieldId: 'timezone',
    required: () => { return true },
  },
  cap_id: {
    fieldId: 'cap_id',
    required: () => { return false },
    toDTO: (schedule, value) => {
      if (!value) {
          return "";
      }
      return value;
    },
  },
  take_window: {
    fieldId: 'take_window',
    required: (schedule) => { return schedule?.offset?.length > 1 },
    toDTO: (schedule, value) => schedule.type === 'daily' ? value : undefined,
  },
  medication: {
    fieldId: 'medication',
    required: () => { return false },
  },
  enrollment_id: {
    fieldId: 'enrollment_id',
    required: () => { return false },
    toDTO: (schedule, value) => {
      if (value === null) {
        return null;
      }
      if (!value) {
        return undefined;
      }
      return value;
    },
  },
  sms_reminder: {
    fieldId: 'sms_reminder',
    required: () => { return false },
    getPropertyForDTO: () => 'sms_reminders',
    toDTO: (schedule, value, defaultReminders) => {
      if (!value) { return undefined; }
      if (schedule.type === 'as_needed') { return undefined; }
      const ret = {};
      Object.entries(value).forEach(([key, v]) => {
        ret[key] = {
          enabled: !!v?.checked,
          text: (v?.text === defaultReminders[key]?.message) ? '' : (v?.text || ''),
        };
        if (v?.offset !== undefined) {
          ret[key].offset = v.offset;
        }
      });
      return ret;
    },
  },
  milestone_reminders: {
    fieldId: 'milestone_reminders',
    required: () => { return false },
    toDTO: (schedule, value, defaultReminders) => {
      if (!value) { return undefined; }
      if (schedule.type === 'as_needed') { return undefined; }
      const ret = {};
      Object.entries(value).forEach(([key, v]) => {
        if (!v?.checked) {
          return;
        }
        ret[key] = {
          text: (v?.text === defaultReminders[key]?.message) ? '' : (v?.text || ''),
        };
        if (v?.thresholds !== undefined) {
          ret[key].thresholds = v.thresholds;
        }
      });
      ret.milestone_rules = value.milestone_rules || undefined;
      if (ret.milestone_rules?.time) {
        ret.milestone_rules.time.thresholds = [7, 14, 21, 28];
        ret.milestone_rules.time.window_days = 7;
        ret.milestone_rules.time.compliant_days_required = 7;
        ret.milestone_rules.time.partial_days_required = 5;
      }
      if (value.streak_achievement?.separateMessagesForThresholds && ret.streak_achievement?.text) {
        delete ret.streak_achievement.text;
      }
      if (!value.streak_achievement?.separateMessagesForThresholds && ret.milestone_rules?.streak?.messages) {
        delete ret.milestone_rules?.streak?.messages;
      }
      ret.time_milestones_enabled = value.time_milestones_enabled;
      ret.time_milestone_compliant_enabled = value.time_milestones_enabled;
      ret.time_milestone_partial_enabled = value.time_milestones_enabled;
      return ret;
    },
  },
};

// The medications tab returns the raw schedule format (no "medication" block); SIG, notes and the patient medication link live there.
export const getMedicationFromFormat = format => {
  const medication = ['sig', 'notes', 'patient_medication', 'ndc_code'].reduce(
    (acc, key) => (format?.[key] ? { ...acc, [key]: format[key] } : acc),
    {},
  );
  return _.isEmpty(medication) ? undefined : medication;
};

export const scheduleToDTO = (schedule, defaultReminders) => {
  return Object.values(scheduleFieldsEnum).reduce((acc, field) => {
    const value = schedule[field.fieldId];
      acc[field.getPropertyForDTO ? field.getPropertyForDTO(schedule) : field.fieldId] = field.toDTO ? field.toDTO(schedule, value, defaultReminders) : value;
    return acc;
  }, {});
};

export const getScheduleDataForBackend = (schedule, originalSchedule, defaultReminders) => {
  const scheduleDTO = scheduleToDTO(schedule, defaultReminders);
  const originalScheduleDTO = originalSchedule ? scheduleToDTO(originalSchedule, defaultReminders) : null;
  if (originalScheduleDTO) {
    const changedFields = _.reduce(scheduleDTO, (acc, value, key) => {
      const originalValue = originalScheduleDTO[key];
      const isEqual = moment.isMoment(value)
        ? moment(originalValue).isSame(value)
        : _.isEqual(value, originalValue);
      if (!isEqual) {
        acc[key] = value;
      }
      return acc;
    }, {});
    return changedFields;
  }
  return scheduleDTO;
};
